import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { onAuthStateChanged } from "firebase/auth";
import { auth, methodologyService, GuidedMethodology, GuidedMethodologyStep, StepCondition } from "../../lib/firebase";
import { GuidedMethodologyEditDialog } from "./GuidedMethodologyEditDialog";
import { ToggleSwitch } from "../../components/ui/toggle-switch";
import { MethodologyVisualization } from "../../components/ui/methodology-visualization";
import { StepEditDialog } from "./StepEditDialog";

const ADMIN_EMAIL = "admin@rawpa.com";

interface GuidedMethodologyDetailParams {
  methodologyId: string;
}

export const GuidedMethodologyDetail = (): JSX.Element => {
  const { methodologyId } = useParams<keyof GuidedMethodologyDetailParams>() as GuidedMethodologyDetailParams;
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [methodology, setMethodology] = useState<GuidedMethodology | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedStep, setSelectedStep] = useState<GuidedMethodologyStep | null>(null);
  const [showVisualization, setShowVisualization] = useState(false);
  const [isEditingStep, setIsEditingStep] = useState(false);
  const [currentStep, setCurrentStep] = useState<GuidedMethodologyStep | null>(null);
  const [visualEditSteps, setVisualEditSteps] = useState<GuidedMethodologyStep[] | null>(null);
  const [visualEditMode, setVisualEditMode] = useState(false);
  const [savingVisualFlow, setSavingVisualFlow] = useState(false);

  // Check authentication
  useEffect(() => {
    if (!auth) {
      setError("Authentication service unavailable");
      setLoading(false);
      return () => {};
    }

    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (!user || user.email !== ADMIN_EMAIL) {
        navigate('/aAyuiOcvMq');
        return;
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, [navigate]);

  // Load methodology
  useEffect(() => {
    const loadMethodology = async () => {
      setLoading(true);
      
      try {
        const data = await methodologyService.getGuidedMethodologyById(methodologyId);
        if (data) {
          setMethodology(data);
        } else {
          setError("Methodology not found");
        }
      } catch (error) {
        console.error("Error loading guided methodology:", error);
        setError("Failed to load guided methodology");
      } finally {
        setLoading(false);
      }
    };

    loadMethodology();
  }, [methodologyId]);

  const handleSaveMethodology = async (data: { name: string; description: string; status: 'Active' | 'Inactive' | 'Draft'; keywords?: string[] }) => {
    setError(null);
    
    try {
      if (!methodology) return;
      
      console.log("Data received from edit dialog:", data);
      console.log("Current methodology state:", methodology);
      
      const updatedMethodology = {
        ...methodology,
        name: data.name,
        description: data.description,
        status: data.status,
        keywords: data.keywords || methodology.keywords || []
      };
      
      console.log("Saving methodology with these values:", updatedMethodology);
      
      const success = await methodologyService.saveGuidedMethodology(updatedMethodology);
      
      if (!success) {
        setError("Failed to save methodology. Please try again.");
      } else {
        setMethodology(updatedMethodology);
        setEditDialogOpen(false);
      }
    } catch (error) {
      console.error('Error saving methodology:', error);
      setError("An unexpected error occurred. Please try again.");
    }
  };

  const handleEditMethodology = () => {
    if (methodology) {
      console.log("Opening edit dialog with: ", {
        name: methodology.name,
        description: methodology.description,
        status: methodology.status,
        keywords: methodology.keywords
      });
      setEditDialogOpen(true);
    }
  };

  // Function to create a new step
  const createNewStep = (): GuidedMethodologyStep => {
    const newStepId = `step-${Date.now()}`;
    return {
      id: newStepId,
      title: "New Step",
      description: "Description of this step",
      toolSuggestions: [],
      toolSuggestionUrls: {},
      commandExamples: [],
      guidanceNotes: "Provide detailed guidance here",
      requiresUserInput: false,
      nextStepType: "Linear",
      nextStepId: undefined,
      conditions: []
    };
  };

  // Handle adding a new step
  const handleAddStep = () => {
    if (!methodology) return;
    
    const newStep = createNewStep();
    setCurrentStep(newStep);
    setIsEditingStep(true);
  };

  // Handle editing a step
  const handleEditStep = (step: GuidedMethodologyStep) => {
    setCurrentStep({...step});
    setIsEditingStep(true);
  };

  // Handle deleting a step
  const handleDeleteStep = async (stepId: string) => {
    if (!methodology) return;
    
    if (!window.confirm("Are you sure you want to delete this step? This action cannot be undone.")) {
      return;
    }
    
    try {
      // Create a new array of steps without the deleted step
      const updatedSteps = methodology.steps.filter(step => step.id !== stepId);
      
      // Also update any nextStepId references that point to the deleted step
      updatedSteps.forEach(step => {
        if (step.nextStepType === 'Linear' && step.nextStepId === stepId) {
          step.nextStepId = undefined; // Point to end (undefined)
        }
        
        if (step.nextStepType === 'Conditional' && step.conditions) {
          step.conditions.forEach(condition => {
            if (condition.nextStepId === stepId) {
              condition.nextStepId = 'end'; // Point to end
            }
          });
        }
      });
      
      const updatedMethodology = {
        ...methodology,
        steps: updatedSteps
      };
      
      const success = await methodologyService.saveGuidedMethodology(updatedMethodology);
      
      if (success) {
        setMethodology(updatedMethodology);
        setSelectedStep(null);
      } else {
        setError("Failed to delete step. Please try again.");
      }
    } catch (err) {
      console.error("Error deleting step:", err);
      setError("An unexpected error occurred while deleting the step.");
    }
  };

  // Handle saving a step (add or edit)
  const handleSaveStep = async (step: GuidedMethodologyStep) => {
    if (!methodology) return;
    
    try {
      // Further sanitize the step to ensure no undefined values
      const sanitizedStep: GuidedMethodologyStep = {
        ...step,
        // Ensure basic properties have default values
        title: step.title || '',
        description: step.description || '',
        toolSuggestions: step.toolSuggestions || [],
        toolSuggestionUrls: step.toolSuggestionUrls || {},
        commandExamples: step.commandExamples || [],
        guidanceNotes: step.guidanceNotes || '',
        requiresUserInput: Boolean(step.requiresUserInput),
        inputFieldLabel: step.requiresUserInput ? (step.inputFieldLabel || 'Enter input') : '',
        inputDataType: step.requiresUserInput ? (step.inputDataType || 'Text') : 'Text',
        nextStepType: step.nextStepType || 'Linear',
        
        // Handle nextStepId based on flow type
        nextStepId: step.nextStepType === 'Linear' 
          ? (step.nextStepId === 'end' || step.nextStepId === null ? undefined : step.nextStepId) 
          : undefined,
          
        // Properly handle conditions
        conditions: step.nextStepType === 'Conditional'
          ? (step.conditions || []).map(c => ({
              id: c.id || `condition-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
              condition: c.condition || 'equals',
              value: c.value || '',
              // Per StepCondition interface, nextStepId must be a string
              nextStepId: c.nextStepId === null ? 'end' : (c.nextStepId || 'end')
            }))
          : []
      };
      
      let updatedSteps: GuidedMethodologyStep[];
      
      if (methodology.steps.some(s => s.id === sanitizedStep.id)) {
        // Edit existing step
        updatedSteps = methodology.steps.map(s => 
          s.id === sanitizedStep.id ? sanitizedStep : s
        );
      } else {
        // Add new step
        updatedSteps = [...methodology.steps, sanitizedStep];
      }
      
      // Create a clean methodology object with no undefined values
      const updatedMethodology = {
        ...methodology,
        steps: updatedSteps,
        name: methodology.name || '',
        description: methodology.description || '', 
        status: methodology.status || 'Draft',
        keywords: methodology.keywords || []
      };
      
      console.log("Saving guided methodology with steps:", updatedMethodology.steps.length);
      const success = await methodologyService.saveGuidedMethodology(updatedMethodology);
      
      if (success) {
        setMethodology(updatedMethodology);
        setIsEditingStep(false);
        setCurrentStep(null);
      } else {
        setError("Failed to save step. Please try again.");
      }
    } catch (err) {
      console.error("Error saving step:", err);
      // Show more detailed error message
      const errorMessage = err instanceof Error ? err.message : "Unknown error";
      setError(`Failed to save step: ${errorMessage}`);
    }
  };

  // Quick status change handler
  const handleStatusChange = async (newStatus: 'Active' | 'Inactive' | 'Draft') => {
    if (!methodology) return;
    
    try {
      const updatedMethodology = {
        ...methodology,
        status: newStatus
      };
      
      const success = await methodologyService.saveGuidedMethodology(updatedMethodology);
      
      if (success) {
        setMethodology(updatedMethodology);
      } else {
        setError("Failed to update methodology status. Please try again.");
      }
    } catch (error) {
      console.error('Error updating methodology status:', error);
      setError("An unexpected error occurred. Please try again.");
    }
  };

  const renderStepFlow = () => {
    if (!methodology || methodology.steps.length === 0) {
      return (
        <div className="text-center py-8 text-gray-500">
          <p>No steps defined for this methodology</p>
          <Button 
            className="mt-4 h-9 px-4 bg-[#daed6e] rounded-lg hover:bg-[#c5d85c] transition-colors"
            onClick={handleAddStep}
          >
            Add First Step
          </Button>
        </div>
      );
    }

    return (
      <div className="mt-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="font-['Space_Grotesk'] font-medium text-lg text-black">Step Flow</h3>
          <Button 
            className="h-8 px-3 bg-[#daed6e] rounded-lg hover:bg-[#c5d85c] transition-colors text-xs"
            onClick={handleAddStep}
          >
            Add New Step
          </Button>
        </div>
        <div className="bg-gray-50 p-4 rounded-lg overflow-x-auto">
          <div className="min-w-[600px]">
            {methodology.steps.map((step, index) => (
              <div 
                key={step.id} 
                className="mb-4 relative"
              >
                <div className={`
                  border rounded-lg p-4 transition-colors
                  ${selectedStep?.id === step.id ? 'border-[#daed6e] bg-[#f7fae5]' : 'border-gray-200 hover:border-gray-300'}
                `}>
                  <div className="flex justify-between items-center">
                    <div 
                      className="flex items-center gap-2 cursor-pointer flex-1"
                      onClick={() => setSelectedStep(step === selectedStep ? null : step)}
                    >
                      <span className="inline-block w-6 h-6 rounded-full bg-gray-200 text-xs flex items-center justify-center">
                        {index + 1}
                      </span>
                      <h4 className="font-medium">{step.title}</h4>
                    </div>
                    <div className="flex gap-2 items-center">
                      <div className="text-sm text-gray-500 mr-2">
                        {step.nextStepType === 'Linear' 
                          ? (step.nextStepId 
                              ? `→ Step ${methodology.steps.findIndex(s => s.id === step.nextStepId) + 1}`
                              : '→ End') 
                          : `→ ${step.conditions?.length || 0} condition(s)`}
                      </div>
                      <Button
                        className="h-7 px-2 bg-[#daed6e] rounded-lg hover:bg-[#c5d85c] transition-colors text-xs"
                        onClick={() => handleEditStep(step)}
                      >
                        Edit
                      </Button>
                      <Button
                        className="h-7 px-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-colors text-xs"
                        onClick={() => handleDeleteStep(step.id)}
                      >
                        Delete
                      </Button>
                    </div>
                  </div>
                  
                  {selectedStep?.id === step.id && (
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <p className="text-sm mb-2">{step.description}</p>
                      
                      {step.toolSuggestions.length > 0 && (
                        <div className="mb-2">
                          <span className="text-xs font-medium text-gray-500">Tools:</span>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {step.toolSuggestions.map((tool, i) => {
                              const toolUrl = step.toolSuggestionUrls?.[tool];
                              
                              return (
                                <div key={i} className="text-xs bg-gray-100 px-2 py-1 rounded flex items-center gap-1">
                                  <span>{tool}</span>
                                  {toolUrl && (
                                    <a 
                                      href={toolUrl}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-blue-500 hover:text-blue-700"
                                      title={`Open ${tool} website`}
                                    >
                                      <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                                        <polyline points="15 3 21 3 21 9"></polyline>
                                        <line x1="10" y1="14" x2="21" y2="3"></line>
                                      </svg>
                                    </a>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                      
                      {step.requiresUserInput && (
                        <div className="mb-2">
                          <span className="text-xs font-medium text-gray-500">User Input:</span>
                          <span className="text-xs ml-2">
                            {step.inputFieldLabel || 'Required'} 
                            {step.inputDataType ? ` (${step.inputDataType})` : ''}
                          </span>
                        </div>
                      )}
                      
                      {step.nextStepType === 'Conditional' && step.conditions && step.conditions.length > 0 && (
                        <div className="mt-2">
                          <span className="text-xs font-medium text-gray-500">Conditions:</span>
                          <ul className="mt-1 space-y-1">
                            {step.conditions.map((condition, i) => (
                              <li key={i} className="text-xs">
                                If {condition.condition} "{condition.value}" → 
                                {condition.nextStepId === 'end' 
                                  ? ' End' 
                                  : ` Step ${methodology.steps.findIndex(s => s.id === condition.nextStepId) + 1}`}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                
                {/* Connection lines */}
                {step.nextStepType === 'Linear' && step.nextStepId && (
                  <div className="h-8 w-px bg-gray-300 absolute left-3 -bottom-8"></div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen w-full bg-[#e1dcdc75] relative">
      {/* Navigation bar */}
      <div className="fixed w-full px-4 top-[46px] z-10">
        <div className="max-w-[1257px] mx-auto h-[77px] relative rounded-[28px]">
          <div className="absolute w-full h-full top-0 left-0 bg-white rounded-[28px] shadow-[0px_4px_4px_#00000040] blur-[3.05px] opacity-[0.53]" />
          <div className="relative h-full flex items-center justify-between px-6">
            <div className="flex items-center gap-4 cursor-pointer" onClick={() => navigate('/aAyuiOcvMq/dashboard')}>
              <img className="w-[27px] h-[27px] object-cover" alt="Beetle" src="/beetle.png" />
              <span className="font-['Space_Grotesk'] font-bold text-black text-[32px]">RAWPA Admin</span>
            </div>
            <div 
              onClick={() => {
                if (auth) auth.signOut().then(() => navigate('/aAyuiOcvMq'));
              }}
              className="w-[48px] h-[48px] bg-[#daed6e] rounded-[12px] flex items-center justify-center cursor-pointer hover:bg-[#c5d85c] transition-colors"
            >
              <img className="w-6 h-6" alt="Sign Out" src="/user-icon.svg" />
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="container mx-auto px-4 pt-[160px] pb-16">
        {error && (
          <div className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded mb-4">
            <p>{error}</p>
          </div>
        )}
        
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-2">
            <button 
              onClick={() => navigate('/aAyuiOcvMq/guided-methodologies')}
              className="text-black opacity-60 hover:opacity-100 transition-opacity"
            >
              ← Back to Guided Methodologies
            </button>
          </div>
          
          <div className="flex gap-2">
            <Button
              onClick={handleEditMethodology}
              className="h-9 px-4 bg-[#daed6e] rounded-lg hover:bg-[#c5d85c] transition-colors"
              disabled={!methodology}
            >
              Edit Methodology
            </Button>
          </div>
        </div>
        
        {loading ? (
          <div className="flex justify-center py-8">
            <div className="loader"></div>
          </div>
        ) : methodology ? (
          <div className="bg-white rounded-[20px] p-6 shadow-lg">
            <div className="mb-6">
              <div className="flex justify-between items-start">
                <div>
                  <h1 className="font-['Space_Grotesk'] font-bold text-2xl text-black mb-2">
                    {methodology.name}
                  </h1>
                  <p className="font-['Space_Grotesk'] text-sm text-black opacity-80 mb-3">
                    {methodology.description || "No description provided"}
                  </p>
                  
                  {/* Display Keywords */}
                  {methodology.keywords && methodology.keywords.length > 0 && (
                    <div className="mb-3">
                      <span className="text-xs font-medium text-gray-600 mr-2">Keywords:</span>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {methodology.keywords.map((keyword, idx) => (
                          <span 
                            key={idx}
                            className="inline-block px-2 py-1 bg-gray-100 text-xs rounded-full"
                          >
                            {keyword}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      <span className={`inline-block px-2 py-1 text-xs rounded-full ${
                        methodology.status === 'Active' 
                          ? 'bg-green-100 text-green-800' 
                          : methodology.status === 'Inactive'
                            ? 'bg-gray-100 text-gray-800'
                            : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {methodology.status}
                      </span>
                      
                      {/* Status change buttons */}
                      <div className="flex gap-1">
                        {methodology.status !== 'Active' && (
                          <button
                            onClick={() => handleStatusChange('Active')}
                            className="text-xs px-2 py-1 bg-green-100 hover:bg-green-200 rounded-md text-green-800"
                            title="Make Active"
                          >
                            Make Active
                          </button>
                        )}
                        {methodology.status !== 'Inactive' && (
                          <button
                            onClick={() => handleStatusChange('Inactive')}
                            className="text-xs px-2 py-1 bg-gray-100 hover:bg-gray-200 rounded-md text-gray-800"
                            title="Make Inactive"
                          >
                            Make Inactive
                          </button>
                        )}
                        {methodology.status !== 'Draft' && (
                          <button
                            onClick={() => handleStatusChange('Draft')}
                            className="text-xs px-2 py-1 bg-yellow-100 hover:bg-yellow-200 rounded-md text-yellow-800"
                            title="Set as Draft"
                          >
                            Set as Draft
                          </button>
                        )}
                      </div>
                    </div>
                    <span className="text-xs text-gray-500">
                      {methodology.steps.length} steps
                    </span>
                  </div>
                </div>
                
                <div className="text-right">
                  <p className="text-xs text-gray-500">
                    Created: {new Date(methodology.createdAt || '').toLocaleDateString()}
                  </p>
                  <p className="text-xs text-gray-500">
                    Last updated: {new Date(methodology.updatedAt || '').toLocaleDateString()}
                  </p>
                </div>
              </div>
            </div>
            
            {/* Visual Flow Editor for Admins */}
            <div className="mb-8">
              <div className="flex justify-between items-center mb-2">
                <span className="font-bold text-lg">Visual Flow Editor</span>
                <button
                  onClick={() => {
                    if (!visualEditMode && methodology) {
                      setVisualEditSteps(methodology.steps.map(s => ({ ...s })));
                    }
                    setVisualEditMode(v => !v);
                  }}
                  className={`px-4 py-2 rounded-lg font-semibold transition-colors ${visualEditMode ? 'bg-gray-200 text-gray-700 hover:bg-gray-300' : 'bg-[#daed6e] text-black hover:bg-[#c5d85c]'}`}
                >
                  {visualEditMode ? 'Exit Edit Mode' : 'Edit Visual Flow'}
                </button>
              </div>
              <div className="relative border rounded-xl bg-[#f7fae5] shadow-inner p-4 min-h-[500px]">
                <MethodologyVisualization
                  methodology={{ ...methodology, steps: visualEditMode && visualEditSteps ? visualEditSteps : methodology?.steps || [] }}
                  editable={visualEditMode}
                  onChange={setVisualEditSteps}
                />
                {visualEditMode && (
                  <div className="absolute top-4 right-4 flex gap-2 z-10">
                    <button
                      onClick={async () => {
                        if (!visualEditSteps || !methodology) return;
                        setSavingVisualFlow(true);
                        try {
                          await methodologyService.saveGuidedMethodology({ ...methodology, steps: visualEditSteps });
                          setVisualEditMode(false);
                          window.location.reload();
                        } catch (e) {
                          alert('Failed to save visual flow.');
                        } finally {
                          setSavingVisualFlow(false);
                        }
                      }}
                      className="px-4 py-2 rounded-lg bg-green-500 text-white font-semibold hover:bg-green-600 transition-colors shadow"
                      disabled={savingVisualFlow}
                    >
                      {savingVisualFlow ? 'Saving...' : 'Save Changes'}
                    </button>
                  </div>
                )}
              </div>
            </div>
            
            {/* Render methodology steps */}
            {renderStepFlow()}
          </div>
        ) : (
          <div className="bg-white rounded-[20px] p-6 shadow-lg">
            <div className="text-center py-8 text-gray-500">
              <p>Methodology not found</p>
            </div>
          </div>
        )}
        
        {/* Edit Dialog */}
        {editDialogOpen && methodology && (
          <GuidedMethodologyEditDialog
            open={editDialogOpen}
            onClose={() => setEditDialogOpen(false)}
            onSave={handleSaveMethodology}
            name={methodology.name}
            description={methodology.description}
            status={methodology.status}
            keywords={methodology.keywords || []}
            isNew={false}
          />
        )}
        
        {/* Step Edit Dialog */}
        {isEditingStep && currentStep && (
          <StepEditDialog
            isOpen={isEditingStep}
            onClose={() => {
              setIsEditingStep(false);
              setCurrentStep(null);
            }}
            onSave={handleSaveStep}
            step={currentStep}
            availableSteps={methodology?.steps || []}
          />
        )}
      </div>
    </div>
  );
}; 